import React, { useCallback, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Paper
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { MAX_FILE_SIZE } from '../../constants/fileConstants';

/**
 * FileUploader - Drag and drop file upload component
 * 
 * This component provides a user-friendly interface for uploading files
 * with drag-and-drop support and validation feedback.
 * 
 * @param {object} props
 * @param {function} props.onFileSelect - Callback when files are selected
 * @param {boolean} props.loading - Whether upload is in progress
 * @param {string} props.error - Error message to display
 * @param {number} props.maxFileSize - Maximum file size in bytes
 * @param {Array<string>} props.allowedMimeTypes - Allowed MIME types
 * @param {boolean} props.multiple - Whether to allow multiple file selection
 * @param {string} props.title - Custom title for the uploader
 * @param {object} props.sx - Additional styles
 */
const FileUploader = ({
  onFileSelect,
  loading = false,
  error = null,
  maxFileSize = MAX_FILE_SIZE,
  allowedMimeTypes = null,
  multiple = true,
  title = "Upload Files",
  sx = {}
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragError, setDragError] = useState(null);
  const fileInputRef = useRef(null);

  /**
   * Validates files before calling onFileSelect
   */
  const validateAndSelectFiles = useCallback((files) => {
    const errors = [];
    const validFiles = [];

    Array.from(files).forEach((file) => {
      if (file.size > maxFileSize) {
        errors.push(`${file.name}: File size exceeds ${(maxFileSize / 1024 / 1024).toFixed(1)}MB`);
        return;
      }

      if (allowedMimeTypes && !allowedMimeTypes.includes(file.type)) {
        errors.push(`${file.name}: File type not allowed`);
        return;
      }

      if (file.type == null || file.type == undefined || file.type == '') {
        errors.push(`${file.name}: File type is empty`);
        return;
      }

      if (file.size === 0) {
        errors.push(`${file.name}: File size is 0`);
        return;
      }

      validFiles.push(file);
    });

    if (errors.length > 0) {
      setDragError(errors.join(', '));
      return;
    }

    setDragError(null);
    onFileSelect(validFiles);
  }, [onFileSelect, maxFileSize, allowedMimeTypes]);

  /**
   * Handles file input change
   */
  const handleFileInputChange = useCallback((event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      validateAndSelectFiles(files);
    }
    // Reset input value to allow selecting the same file again
    event.target.value = '';
  }, [validateAndSelectFiles]);

  /**
   * Handles drag events
   */
  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    setIsDragOver(false);
    
    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      validateAndSelectFiles(files);
    }
  }, [validateAndSelectFiles]);

  /**
   * Opens file dialog
   */
  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  /**
   * Gets allowed file types string for input
   */
  const getAcceptedTypes = useCallback(() => {
    if (!allowedMimeTypes) return '';
    return allowedMimeTypes.join(',');
  }, [allowedMimeTypes]);

  return (
    <Box sx={{ ...sx }}>
      <input
        ref={fileInputRef}
        type="file"
        multiple={multiple}
        accept={getAcceptedTypes()}
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
      />
      
      <Paper
        sx={{
          border: '2px dashed',
          borderColor: isDragOver ? 'primary.main' : 'divider',
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          backgroundColor: isDragOver ? 'action.hover' : 'background.paper',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover'
          }
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary">
              Uploading files...
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main' }} />
            <Typography variant="h6" gutterBottom>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Drag and drop files here, or click to browse
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={(e) => {
                e.stopPropagation();
                handleClick();
              }}
            >
              Select Files
            </Button>
            {allowedMimeTypes && (
              <Typography variant="caption" color="text.secondary">
                Allowed types: {allowedMimeTypes.join(', ')}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              Max size: {(maxFileSize / 1024 / 1024).toFixed(1)}MB
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Error display */}
      {(error || dragError) && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error || dragError}
        </Alert>
      )}
    </Box>
  );
};

export default FileUploader; 